import React from 'react';
import { RACES, ECON, mineralIncome, gasIncome, formatNum } from './sc2Data';

function Slider({ label, value, min, max, onChange, hint }) {
  return (
    <div className="sc2-slider">
      <div className="sc2-slider-head">
        <label>{label}</label>
        <span className="sc2-slider-value">{value}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      {hint && <div className="sc2-slider-hint">{hint}</div>}
    </div>
  );
}

function IncomeCalculator({ raceId, econ, setEcon }) {
  const race = RACES[raceId];
  const { bases, mineralWorkers, gasWorkers } = econ;

  const minerals = mineralIncome(mineralWorkers, bases);
  const gas = gasIncome(gasWorkers, bases);
  const muleIncome = raceId === 'terran' ? econ.orbitals * ECON.MULE_PER_MIN : 0;
  const totalMinerals = minerals + muleIncome;

  const optimalMineral = ECON.OPTIMAL_MINERAL_WORKERS_PER_BASE * bases;
  const maxMineral = ECON.MAX_MINERAL_WORKERS_PER_BASE * bases;
  const maxGas = ECON.MAX_GAS_WORKERS_PER_GEYSER * ECON.GEYSERS_PER_BASE * bases;

  const saturationPct = Math.min(100, Math.round((mineralWorkers / optimalMineral) * 100));
  const overSaturated = mineralWorkers > maxMineral;

  const set = (patch) => setEcon({ ...econ, ...patch });

  return (
    <div>
      <h2 className="sc2-section-title" style={{ color: race.color }}>
        {race.icon} Калькулятор добычи: {race.ru}
      </h2>

      <div className="sc2-two-col">
        <div className="sc2-panel">
          <h3>Ваша экономика</h3>
          <Slider label="Базы" value={bases} min={1} max={6} onChange={(v) => set({ bases: v })} />
          <Slider
            label="Рабочие на минералах"
            value={mineralWorkers}
            min={0}
            max={90}
            onChange={(v) => set({ mineralWorkers: v })}
            hint={`Оптимум: ${optimalMineral} (16/базу) · Максимум: ${maxMineral} (24/базу)`}
          />
          <Slider
            label="Рабочие на газе"
            value={gasWorkers}
            min={0}
            max={maxGas}
            onChange={(v) => set({ gasWorkers: v })}
            hint={`Максимум: ${maxGas} (3 на гейзер, ${ECON.GEYSERS_PER_BASE} гейзера на базу)`}
          />
          {raceId === 'terran' && (
            <Slider
              label="Орбитальные станции (MULE)"
              value={econ.orbitals}
              min={0}
              max={6}
              onChange={(v) => set({ orbitals: v })}
              hint={`Каждая орбиталка ≈ +${ECON.MULE_PER_MIN} минералов/мин за счёт MULE`}
            />
          )}
        </div>

        <div className="sc2-panel">
          <h3>Доход в минуту</h3>
          <div className="sc2-income-cards">
            <div className="sc2-income-card minerals">
              <div className="sc2-income-value">💎 {formatNum(totalMinerals)}</div>
              <div className="sc2-income-label">
                минералов/мин
                {muleIncome > 0 && <span> (из них MULE: {formatNum(muleIncome)})</span>}
              </div>
            </div>
            <div className="sc2-income-card gas">
              <div className="sc2-income-value">🟢 {formatNum(gas)}</div>
              <div className="sc2-income-label">газа/мин</div>
            </div>
          </div>

          <div className="sc2-saturation">
            <div className="sc2-saturation-head">
              <span>Насыщение минеральных линий</span>
              <span>{saturationPct}%</span>
            </div>
            <div className="sc2-progress">
              <div
                className="sc2-progress-fill"
                style={{ width: `${saturationPct}%`, background: saturationPct >= 100 ? '#28a745' : race.color }}
              />
            </div>
            {overSaturated && (
              <div className="sc2-warn">
                ⚠️ Больше {maxMineral} рабочих на минералах не добавляют дохода — пора расширяться!
              </div>
            )}
            {!overSaturated && mineralWorkers > optimalMineral && (
              <div className="sc2-hint-box">
                ℹ️ Рабочие сверх {optimalMineral} приносят лишь ~{ECON.MINERALS_PER_EXTRA_WORKER} мин/мин вместо ~{ECON.MINERALS_PER_WORKER}. Это запас для перевода на новую базу.
              </div>
            )}
          </div>

          <table className="sc2-table compact">
            <thead>
              <tr><th>Правило</th><th>Значение</th></tr>
            </thead>
            <tbody>
              <tr><td>1-й и 2-й рабочий на кристалле</td><td>~{ECON.MINERALS_PER_WORKER} мин/мин каждый</td></tr>
              <tr><td>3-й рабочий на кристалле</td><td>~{ECON.MINERALS_PER_EXTRA_WORKER} мин/мин</td></tr>
              <tr><td>Гейзер (3 рабочих)</td><td>~163 газа/мин</td></tr>
              <tr><td>Полная база (16 мин + 6 газ)</td><td>~{formatNum(mineralIncome(16, 1))} 💎 + ~{formatNum(gasIncome(6, 1))} 🟢 в мин</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="sc2-note">
        Эталон: ~22 рабочих на базу (16 минералы + 6 газ). Стройте рабочих без остановки до ~70–80 на 3–4 базах.
      </p>
    </div>
  );
}

export default IncomeCalculator;
